/**
 * Un componente por paso del asistente (WIZARD_STEPS en @rat/shared). Cada
 * uno recibe `draft` (el WizardDraft completo) y `onChange` (patch parcial)
 * -- sin estado propio, todo vive en ActivityWizard.tsx.
 *
 * Los catalogos (categorias, bases juridicas, destinatarios, etc.) ya NO
 * son un import estatico de @rat/shared -- son administrables
 * (CatalogsAdmin.tsx, src/catalog-admin.ts en el backend) y llegan via el
 * prop `catalogs`, con el fallback estatico solo como respaldo mientras
 * carga o si /catalogs falla (ver FALLBACK_CATALOGS en ActivityWizard.tsx).
 */
import type { WizardDraft } from '@rat/shared';
import { RETENTION_UNIT_LABEL, RETENTION_UNITS } from '@rat/shared';
import type { PublicCatalogItem, PublicCatalogs } from '../../lib/queries';
import { CheckList, Field, SelectInput, TextArea, TextInput, Toggle } from './fields';

export interface StepProps {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}

const isSensitive = (i: PublicCatalogItem) => i.is_sensitive === true;
const isProtectedGroup = (i: PublicCatalogItem) => i.is_protected_group === true;
const requiresReinforced = (i: PublicCatalogItem) => i.requires_reinforced === true;

export function StepIdentificacion({
  draft,
  onChange,
  units,
  unitLocked,
}: StepProps & { units: Array<{ id: string; name: string }>; unitLocked: boolean }) {
  return (
    <>
      <Field label="Nombre de la actividad" required>
        <TextInput value={draft.title} onChange={(e) => onChange({ title: e.target.value })} maxLength={300} />
      </Field>
      <Field label="Unidad responsable" required hint={unitLocked ? 'No se puede cambiar después de crear la actividad.' : undefined}>
        <SelectInput
          value={draft.responsible_unit_id ?? ''}
          disabled={unitLocked}
          onChange={(e) => onChange({ responsible_unit_id: e.target.value || null })}
        >
          <option value="">Seleccione…</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Persona responsable interna" required hint="Quién opera o supervisa el tratamiento dentro de la unidad.">
        <TextInput
          value={draft.operational_owner_name}
          onChange={(e) => onChange({ operational_owner_name: e.target.value })}
          maxLength={200}
        />
      </Field>
    </>
  );
}

export function StepFinalidad({ draft, onChange }: StepProps) {
  return (
    <>
      <Field label="Finalidad del tratamiento" required hint="Para qué se recolectan y usan estos datos.">
        <TextArea value={draft.purpose} onChange={(e) => onChange({ purpose: e.target.value })} maxLength={4000} />
      </Field>
      <Field label="Descripción del proceso" required hint="Cómo opera el tratamiento, paso a paso.">
        <TextArea value={draft.description} onChange={(e) => onChange({ description: e.target.value })} maxLength={8000} />
      </Field>
      <Field label="Origen de los datos" required>
        <TextInput value={draft.data_source} onChange={(e) => onChange({ data_source: e.target.value })} maxLength={2000} />
      </Field>
    </>
  );
}

export function StepTitulares({ draft, onChange, catalogs }: StepProps & { catalogs: PublicCatalogs }) {
  return (
    <>
      <Field label="Categorías de titulares" required hint="A quiénes pertenecen los datos tratados.">
        <CheckList
          options={catalogs.subject_category.map((c) => ({ code: c.code, label: c.label, badge: isProtectedGroup(c) ? 'grupo protegido' : undefined }))}
          selected={draft.subject_categories}
          onChange={(subject_categories) => onChange({ subject_categories })}
        />
      </Field>
      <Field label="Categorías de datos" required hint="Qué tipo de datos se tratan.">
        <CheckList
          options={catalogs.data_category.map((c) => ({ code: c.code, label: c.label, badge: isSensitive(c) ? 'sensible' : undefined }))}
          selected={draft.data_categories}
          onChange={(data_categories) => onChange({ data_categories })}
        />
      </Field>
    </>
  );
}

export function StepBaseJuridica({ draft, onChange, catalogs }: StepProps & { catalogs: PublicCatalogs }) {
  const selectedCodes = draft.legal_bases.map((b) => b.legal_basis);
  const toggle = (code: string) => {
    if (selectedCodes.includes(code)) {
      onChange({ legal_bases: draft.legal_bases.filter((b) => b.legal_basis !== code) });
    } else {
      onChange({ legal_bases: [...draft.legal_bases, { legal_basis: code, justification: '' }] });
    }
  };
  return (
    <Field label="Bases de licitud" required hint="Puede indicar más de una.">
      <div style={{ display: 'grid', gap: 8 }}>
        {catalogs.legal_basis.map((lb) => {
          const entry = draft.legal_bases.find((b) => b.legal_basis === lb.code);
          return (
            <div key={lb.code} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!entry} onChange={() => toggle(lb.code)} />
                <span style={{ flex: 1 }}>{lb.label}</span>
                {requiresReinforced(lb) && (
                  <span className="pill" style={{ background: 'var(--critical-weak)', color: 'var(--critical)', fontSize: 11 }}>
                    reforzada
                  </span>
                )}
              </label>
              {entry && (
                <TextInput
                  placeholder="Justificación (opcional)"
                  value={entry.justification}
                  onChange={(e) =>
                    onChange({
                      legal_bases: draft.legal_bases.map((b) =>
                        b.legal_basis === lb.code ? { ...b, justification: e.target.value } : b,
                      ),
                    })
                  }
                  style={{ marginTop: 6 }}
                />
              )}
            </div>
          );
        })}
      </div>
    </Field>
  );
}

export function StepDestinatarios({ draft, onChange, catalogs }: StepProps & { catalogs: PublicCatalogs }) {
  const addRecipient = () =>
    onChange({ recipients: [...draft.recipients, { recipient_type: catalogs.recipient_type[0]?.code ?? '', name: '', is_processor: false, detail: '' }] });
  const addTransfer = () => onChange({ transfers: [...draft.transfers, { country: '', guarantee_type: undefined, detail: '' }] });

  return (
    <>
      <Field label="Destinatarios" required hint='Quién recibe o accede a los datos (puede ser "uso interno").'>
        <div style={{ display: 'grid', gap: 8 }}>
          {draft.recipients.map((r, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 8, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <SelectInput
                  value={r.recipient_type}
                  onChange={(e) =>
                    onChange({ recipients: draft.recipients.map((x, j) => (j === i ? { ...x, recipient_type: e.target.value } : x)) })
                  }
                  style={{ flex: 1 }}
                >
                  {catalogs.recipient_type.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.label}
                    </option>
                  ))}
                </SelectInput>
                <button className="btn btn--ghost" onClick={() => onChange({ recipients: draft.recipients.filter((_, j) => j !== i) })}>
                  Quitar
                </button>
              </div>
              <TextInput
                placeholder="Nombre (opcional)"
                value={r.name}
                onChange={(e) => onChange({ recipients: draft.recipients.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
              />
              <Toggle
                checked={r.is_processor}
                onChange={(v) => onChange({ recipients: draft.recipients.map((x, j) => (j === i ? { ...x, is_processor: v } : x)) })}
                label="Actúa como encargado de tratamiento"
              />
            </div>
          ))}
          <button className="btn" onClick={addRecipient}>
            + Agregar destinatario
          </button>
        </div>
      </Field>

      <Field label="Transferencias internacionales" hint="Deje vacío si no hay transferencias fuera de Chile.">
        <div style={{ display: 'grid', gap: 8 }}>
          {draft.transfers.map((t, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 8, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <TextInput
                  placeholder="País de destino"
                  value={t.country}
                  onChange={(e) => onChange({ transfers: draft.transfers.map((x, j) => (j === i ? { ...x, country: e.target.value } : x)) })}
                  style={{ flex: 1 }}
                />
                <button className="btn btn--ghost" onClick={() => onChange({ transfers: draft.transfers.filter((_, j) => j !== i) })}>
                  Quitar
                </button>
              </div>
              <SelectInput
                value={t.guarantee_type ?? ''}
                onChange={(e) =>
                  onChange({ transfers: draft.transfers.map((x, j) => (j === i ? { ...x, guarantee_type: e.target.value || undefined } : x)) })
                }
              >
                <option value="">Garantía…</option>
                {catalogs.transfer_guarantee_type.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.label}
                  </option>
                ))}
              </SelectInput>
            </div>
          ))}
          <button className="btn" onClick={addTransfer}>
            + Agregar transferencia
          </button>
        </div>
      </Field>
    </>
  );
}

export function StepConservacion({ draft, onChange, catalogs }: StepProps & { catalogs: PublicCatalogs }) {
  return (
    <>
      <Field label="Criterio de conservación" required>
        <SelectInput value={draft.retention_criterion ?? ''} onChange={(e) => onChange({ retention_criterion: e.target.value || null })}>
          <option value="">Seleccione…</option>
          {catalogs.retention_criterion.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </SelectInput>
      </Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="Plazo">
          <TextInput
            type="number"
            min={1}
            value={draft.retention_value ?? ''}
            onChange={(e) => onChange({ retention_value: e.target.value ? Number(e.target.value) : null })}
          />
        </Field>
        <Field label="Unidad">
          <SelectInput value={draft.retention_unit ?? ''} onChange={(e) => onChange({ retention_unit: (e.target.value || null) as WizardDraft['retention_unit'] })}>
            <option value="">—</option>
            {RETENTION_UNITS.map((u) => (
              <option key={u} value={u}>
                {RETENTION_UNIT_LABEL[u]}
              </option>
            ))}
          </SelectInput>
        </Field>
      </div>
      <Field label="Notas de conservación" hint="Detalle adicional (opcional).">
        <TextArea value={draft.retention_text} onChange={(e) => onChange({ retention_text: e.target.value })} maxLength={2000} />
      </Field>
    </>
  );
}

export function StepAutomatizacion({ draft, onChange }: StepProps) {
  const addDecision = () =>
    onChange({
      automated_decisions: [...draft.automated_decisions, { description: '', stage: '', human_in_the_loop: true, produces_profiling: false }],
    });
  return (
    <>
      <Toggle
        checked={draft.has_automated_decision}
        onChange={(v) => onChange({ has_automated_decision: v, automated_decisions: v ? draft.automated_decisions : [] })}
        label="El tratamiento incluye decisiones automatizadas"
      />
      <Toggle checked={draft.uses_ai} onChange={(uses_ai) => onChange({ uses_ai })} label="Se utiliza inteligencia artificial" />

      {draft.has_automated_decision && (
        <Field label="Decisiones automatizadas" required>
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {draft.automated_decisions.map((d, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 8, display: 'grid', gap: 6 }}>
                <TextArea
                  placeholder="Describa la decisión automatizada"
                  rows={2}
                  value={d.description}
                  onChange={(e) =>
                    onChange({ automated_decisions: draft.automated_decisions.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })
                  }
                />
                <TextInput
                  placeholder="Etapa del proceso (opcional)"
                  value={d.stage}
                  onChange={(e) =>
                    onChange({ automated_decisions: draft.automated_decisions.map((x, j) => (j === i ? { ...x, stage: e.target.value } : x)) })
                  }
                />
                <Toggle
                  checked={d.human_in_the_loop}
                  onChange={(v) =>
                    onChange({ automated_decisions: draft.automated_decisions.map((x, j) => (j === i ? { ...x, human_in_the_loop: v } : x)) })
                  }
                  label="Hay intervención humana en la decisión"
                />
                <Toggle
                  checked={d.produces_profiling}
                  onChange={(v) =>
                    onChange({ automated_decisions: draft.automated_decisions.map((x, j) => (j === i ? { ...x, produces_profiling: v } : x)) })
                  }
                  label="Genera perfilamiento"
                />
                <button
                  className="btn btn--ghost"
                  onClick={() => onChange({ automated_decisions: draft.automated_decisions.filter((_, j) => j !== i) })}
                >
                  Quitar
                </button>
              </div>
            ))}
            <button className="btn" onClick={addDecision}>
              + Agregar decisión
            </button>
          </div>
        </Field>
      )}

      <Field label="Sistemas propios" hint="Si desarrolla o mantiene sistemas propios para este tratamiento (opcional).">
        <TextArea value={draft.own_systems_text} onChange={(e) => onChange({ own_systems_text: e.target.value })} maxLength={2000} />
      </Field>
    </>
  );
}

export function StepSeguridad({ draft, onChange, catalogs }: StepProps & { catalogs: PublicCatalogs }) {
  return (
    <Field label="Medidas de seguridad" required>
      <CheckList
        options={catalogs.security_measure.map((m) => ({ code: m.code, label: m.label }))}
        selected={draft.security_measures}
        onChange={(security_measures) => onChange({ security_measures })}
      />
    </Field>
  );
}
